import type { HubNavItem, MenuEntry, ScreenDefinition, SectionBinding } from "./binding";
import { getAt, getString } from "../payload/getAt";
import type { BuxusOperatorPayload } from "../payload/types";
import {
  assetIdAndName,
  contactRowVisibleForJurisdictions,
  countResourceMenuRows,
  getVisibleAssets,
  legacyOperatorScreenGate,
  type AssetVisibilityContext,
} from "./legacyOperatorGates";
import {
  DEFAULT_CONTACT_AREA,
  CONTACT_INLINE_CARD_THRESHOLD,
  contactAreaLabel,
  compareContactsForDisplay,
} from "./contactHelpers";
import { formatPhoneDisplay, phoneTelHref } from "../phone/formatPhoneDisplay";
import { menuTree, screens, MAP_PIPELINE_DISCLAIMER_HTML, MAP_PIPELINE_DISCLAIMER_TEXT } from "./sitemap";
import { compactRichHtml } from "../html/compactRichHtml";
import {
  buildAssetDirectoryListBlocks,
  buildAssetDocumentsListBlocks,
  buildAssetDocumentsListModel,
  type AssetDocumentsListModel,
  buildAssetEmergencyCompanyBlocks,
  hasAssetEmergencyCompanyScreenContent,
  buildAssetEmergencyCommunicationsBlocks,
  buildAssetEmergencyFireSuppressionBlocks,
  buildAssetEmergencyHubNavItems,
  buildAssetEmergencyKeyLocationsBlocks,
  buildAssetEmergencyReportBlocks,
  buildAssetEmergencyResponderBlocks,
  buildAssetEmergencyStepsBlocks,
  buildAssetEmergencyWorstCaseBlocks,
  buildAssetGeneralHazardsBlocks,
  buildAssetHazardDetailModel,
  buildAssetOverviewHubNav,
  buildAssetProfileDetailsHtml,
  buildAssetSpecialNotesBlocks,
  buildFacilityProfileCardModel,
  buildProductsHazardsBrowse,
  assetProfileNavLabel,
  type HazardDetailModel,
  type ProductsHazardsBrowseModel,
  checkHasHazardNavAsset,
  findActiveAssetRow,
  findHazardRowInAsset,
  hasAssetDocumentsNavVisible,
  hazardDetailTitleFromRow,
  markerImageRowVisible,
} from "./assetScreenHelpers";
import {
  hasAnyMakeARequestAccess,
  operatorEmergencyNumber,
  visibleMakeARequestOptions,
} from "./makeRequestHelpers";

/** Session + visibility hooks for `resolveScreen` / `isScreenVisible` (stakeholder, jurisdiction, local files). */
export type ResolveContext = AssetVisibilityContext;

type VisibilityState = {
  cache: Map<string, boolean>;
  visiting: Set<string>;
};

/** Normalized contact row for web/RN — same shape from shared resolver. */
export type ResolvedContactCard = {
  id?: string;
  displayName: string;
  lastName?: string;
  title?: string;
  contactType?: string;
  contactArea?: string;
  email?: string;
  officePhone?: string;
  cellPhone?: string;
  hourEmergency?: string;
  emergencyNumber?: string;
  emergencyPhoneNumber?: string;
  url?: string;
  fileExt?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
};

export type ResolvedNavItem = {
  label: string;
  targetScreenId: string;
  /** When set, shell opens target screen in asset context (e.g. Asset Overview). */
  assetId?: string;
  /** Appends `?ca=` when navigating (Contacts area hub). */
  contactArea?: string;
  /** Appends `?ci=` when navigating (Contact profile). */
  contactId?: string;
};

export type ResolvedBlock =
  | { kind: "richHtml"; html: string }
  | { kind: "noticeCard"; message: string; tone: "warning" | "neutral" }
  | { kind: "links"; items: { label: string; url?: string }[] }
  | { kind: "ctaLink"; label: string; url: string }
  | {
      kind: "resourceSection";
      title: string;
      items: { label: string; url: string }[];
    }
  | {
      kind: "documentSection";
      title: string;
      items: { id: string; label: string; fileExt?: string; fileName?: string }[];
    }
  | {
      kind: "companyErpPicker";
      items: { id: string; label: string; fileExt?: string; fileName?: string }[];
    }
  | {
      kind: "companyResponseAccordions";
      sections: import("./assetScreenHelpers").CompanyResponseAccordionSection[];
    }
  | {
      kind: "responderActionsAccordions";
      sections: import("./assetScreenHelpers").ResponderActionsAccordionSection[];
    }
  | {
      kind: "radioFrequencyAccordions";
      sections: import("./assetScreenHelpers").RadioFrequencyAccordionSection[];
    }
  | {
      kind: "keyLocationAccordions";
      sections: import("./assetScreenHelpers").KeyLocationAccordionSection[];
    }
  | { kind: "emergencyCallButton"; phone: string }
  | {
      kind: "gallery";
      items: { caption: string; ref: { id: string; fileExt?: string } }[];
    }
  | {
      kind: "childNav";
      items: ResolvedNavItem[];
    }
  | {
      kind: "branchHub";
      /** Stakeholder-filtered intro HTML when present for the current session. */
      introHtml?: string;
      items: ResolvedNavItem[];
    }
  | { kind: "contactCards"; items: ResolvedContactCard[] }
  | { kind: "corporateContactCard"; label: string; displayName: string; contactId: string }
  | {
      kind: "makeARequestHub";
      emergencyNumber?: string;
      buttons: { requestType: string; label: string }[];
    }
  | { kind: "requestForm"; requestType: string }
  | { kind: "hubExternalLink"; label: string; url: string }
  | { kind: "productsHazardsBrowse"; model: ProductsHazardsBrowseModel }
  | { kind: "hazardDetail"; model: HazardDetailModel }
  | { kind: "assetDocumentsList"; model: AssetDocumentsListModel }
  | {
      kind: "facilityProfileCard";
      model: import("./assetScreenHelpers").FacilityProfileCardModel;
    };

export type {
  ProductsHazardsBrowseModel,
  HazardBrowseRow,
  HazardBrowseSection,
  HazardDetailModel,
  HazardDetailSection,
  HazardDetailFieldRow,
  HazardDetailMixtureRow,
  HazardDetailLeakImage,
  AssetDocumentsListModel,
  AssetDocumentsSection,
  AssetDocumentsFileRow,
  CompanyResponseAccordionSection,
  FacilityProfileCardModel,
} from "./assetScreenHelpers";

export type ResolveScreenOptions = {
  activeAssetId?: string | null;
  /** Single hazard from Products & Hazards browse (`hi` query on web). */
  hazardId?: string | null;
  /** Contacts area hub (`ca` query on web). */
  contactArea?: string | null;
  /** Single contact profile (`ci` query on web). */
  contactId?: string | null;
  /** Make a Request form type (`rt` query on web). */
  requestType?: string | null;
};

function rowVisible(row: { stakeholders?: unknown }, stakeholderCode: string | null): boolean {
  if (stakeholderCode == null) return true;
  const raw = row as Record<string, unknown>;
  const s = raw.stakeholders ?? raw.Stakeholders;
  if (s == null || !Array.isArray(s) || s.length === 0) return true;
  return s.includes(stakeholderCode);
}

function asRows(value: unknown): { content?: string; stakeholders?: unknown }[] {
  if (!Array.isArray(value)) return [];
  return value as { content?: string; stakeholders?: unknown }[];
}

function rowContent(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const v = raw.content ?? raw.Content ?? raw.html ?? raw.Html;
  return typeof v === "string" ? v : "";
}

function stakeholderIntroHtml(
  payload: BuxusOperatorPayload,
  path: string,
  mergeRows: boolean,
  ctx: ResolveContext,
): string | undefined {
  const parts = asRows(getAt(payload, path))
    .filter((r) => rowVisible(r, ctx.stakeholderCode))
    .map((r) => rowContent(r))
    .filter((html) => !!html.trim());
  if (parts.length === 0) return undefined;
  const raw = mergeRows
    ? parts.join(
        '<hr style="margin:1.25rem 0;border:none;border-top:1px solid #e2e8f0"/>',
      )
    : parts[0]!;
  return compactRichHtml(raw);
}

function resolveHubNavItems(
  items: HubNavItem[],
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  isScreenVisible: (screenId: string) => boolean,
  options?: ResolveScreenOptions,
): ResolvedNavItem[] {
  const visible = isScreenVisible
    ? items.filter(
        (i) => (i.assetId && i.assetId.length > 0) || isScreenVisible(i.targetScreenId),
      )
    : items;
  return visible.map((i) => {
    const inferredAssetId =
      i.assetId ??
      (options?.activeAssetId &&
      typeof options.activeAssetId === "string" &&
      options.activeAssetId.length > 0 &&
      i.targetScreenId.startsWith("screen.asset") &&
      i.targetScreenId !== "screen.assets"
        ? options.activeAssetId
        : undefined);
    const label =
      i.targetScreenId === "screen.testYourKnowledge"
        ? testYourKnowledgeNavLabel(payload, ctx) ?? i.label
        : i.label;
    return {
      label,
      targetScreenId: i.targetScreenId,
      ...(inferredAssetId ? { assetId: inferredAssetId } : {}),
    };
  });
}

function rowLabel(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const v = raw.label ?? raw.Label ?? raw.title ?? raw.Title;
  return typeof v === "string" ? v : "";
}

function rowUrl(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const v = raw.url ?? raw.URL ?? raw.Url;
  return typeof v === "string" ? v : "";
}

function htmlEscape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalizeWebUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return t;
  if (/^mailto:/i.test(t) || /^tel:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function getScalar(op: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = op[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return undefined;
}

function firstStringAtPaths(payload: BuxusOperatorPayload, paths: string[]): string | undefined {
  for (const p of paths) {
    const v = getString(payload, p)?.trim();
    if (v) return v;
  }
  return undefined;
}

function operatorHasMailingAddress(operator: Record<string, unknown>): boolean {
  const county = operator.county;
  if (county != null && typeof county === "object") return true;
  return !!getScalar(operator, "addressLine1", "AddressLine1");
}

function getCompanyOverviewDetailsHtml(payload: BuxusOperatorPayload): string | null {
  const operator = payload.operator as Record<string, unknown>;
  const parts: string[] = [];

  const website = getScalar(operator, "website", "Website");
  if (website) {
    const href = normalizeWebUrl(website);
    parts.push(
      `<div class="co-field"><div class="co-field-label">Website</div><div class="co-field-value"><a href="${htmlEscape(href)}" target="_blank" rel="noopener noreferrer">${htmlEscape(website)}</a></div></div>`,
    );
  }

  const email = getScalar(operator, "email", "Email");
  if (email) {
    const href = email.startsWith("mailto:") ? email : `mailto:${email}`;
    parts.push(
      `<div class="co-field"><div class="co-field-label">Email</div><div class="co-field-value"><a href="${htmlEscape(href)}">${htmlEscape(email)}</a></div></div>`,
    );
  }

  const phone = getScalar(operator, "phoneNumber", "PhoneNumber");
  if (phone) {
    const display = formatPhoneDisplay(phone);
    const href = phoneTelHref(phone);
    parts.push(
      `<div class="co-field"><div class="co-field-label">Phone</div><div class="co-field-value"><a href="${htmlEscape(href)}">${htmlEscape(display)}</a></div></div>`,
    );
  }

  const emergency = getScalar(operator, "emergencyNumber", "EmergencyNumber");
  if (emergency) {
    const display = formatPhoneDisplay(emergency);
    const href = phoneTelHref(emergency);
    parts.push(
      `<div class="co-field"><div class="co-field-label">24hr Emergency Number</div><div class="co-field-value"><a href="${htmlEscape(href)}">${htmlEscape(display)}</a></div></div>`,
    );
  }

  if (operatorHasMailingAddress(operator)) {
    const a1 = getScalar(operator, "addressLine1", "AddressLine1");
    const a2 = getScalar(operator, "addressLine2", "AddressLine2");
    const city = getScalar(operator, "city", "City");
    const zip = getScalar(operator, "zip", "Zip");
    const county = operator.county as Record<string, unknown> | undefined;
    const state = county && typeof county.state === "string" ? county.state : undefined;
    const lines: string[] = [];
    if (a1) lines.push(a1);
    if (a2) lines.push(a2);
    const cityLine = [city, state].filter(Boolean).join(", ");
    if (cityLine || zip) {
      lines.push([cityLine, zip].filter(Boolean).join(" "));
    }
    if (lines.length) {
      parts.push(
        `<div class="co-field"><div class="co-field-label">Corporate Mailing Address</div><div class="co-field-value co-field-address">${lines.map((l) => `<div class="co-field-value-line">${htmlEscape(l)}</div>`).join("")}</div></div>`,
      );
    }
  }

  if (parts.length === 0) return null;
  return `<div class="company-overview-details">${parts.join("")}</div>`;
}

function contactLastName(raw: Record<string, unknown>): string | undefined {
  const direct = raw.lastName ?? raw.LastName;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const nameObj = raw.name ?? raw.Name;
  if (nameObj && typeof nameObj === "object") {
    const n = nameObj as Record<string, unknown>;
    const v = n.lastname ?? n.Lastname;
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function normalizeContactCard(row: unknown): ResolvedContactCard | null {
  if (!row || typeof row !== "object") return null;
  const raw = row as Record<string, unknown>;
  const id = typeof raw.id === "string" ? raw.id : undefined;

  const nameObj = raw.name ?? raw.Name;
  let displayName = "";
  if (nameObj && typeof nameObj === "object") {
    const n = nameObj as Record<string, unknown>;
    const dv = n.displayvalue ?? n.Displayvalue;
    if (typeof dv === "string" && dv.trim()) displayName = dv.trim();
    else {
      const a = [n.firstname, n.lastname, n.Firstname, n.Lastname]
        .map((x) => (typeof x === "string" ? x.trim() : ""))
        .filter(Boolean);
      displayName = a.join(" ");
    }
  }
  if (!displayName) {
    const parts = [raw.firstName, raw.lastName, raw.FirstName, raw.LastName]
      .map((x) => (typeof x === "string" ? x.trim() : ""))
      .filter(Boolean);
    displayName = parts.join(" ");
  }
  if (!displayName) return null;

  const county = raw.county ?? raw.County;
  const stateFromCounty =
    county && typeof county === "object" && typeof (county as Record<string, unknown>).state === "string"
      ? ((county as Record<string, unknown>).state as string)
      : undefined;

  const emergencyNumber =
    getScalar(raw, "emergencyNumber", "EmergencyNumber") ??
    getScalar(raw, "hourEmergency", "HourEmergency") ??
    getScalar(raw, "emergencyPhoneNumber", "EmergencyPhoneNumber");

  return {
    id,
    displayName,
    lastName: contactLastName(raw),
    title: getScalar(raw, "title", "Title"),
    contactType: getScalar(raw, "contactType", "ContactType"),
    contactArea: contactAreaLabel(raw),
    email: getScalar(raw, "email", "Email"),
    officePhone: getScalar(raw, "officePhone", "OfficePhone"),
    cellPhone: getScalar(raw, "cellPhone", "CellPhone"),
    hourEmergency: getScalar(raw, "hourEmergency", "HourEmergency"),
    emergencyNumber,
    emergencyPhoneNumber: getScalar(raw, "emergencyPhoneNumber", "EmergencyPhoneNumber"),
    url: getScalar(raw, "url", "Url"),
    fileExt: getScalar(raw, "fileExt", "FileExt"),
    addressLine1: getScalar(raw, "addressLine1", "AddressLine1"),
    addressLine2: getScalar(raw, "addressLine2", "AddressLine2"),
    city: getScalar(raw, "city", "City"),
    state: getScalar(raw, "state", "State") ?? stateFromCounty,
    zip: getScalar(raw, "zip", "Zip"),
  };
}

function tykTitle(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const v = raw.linkTitle ?? raw.LinkTitle ?? raw.label ?? raw.Label;
  return typeof v === "string" ? v.trim() : "";
}

function tykUrl(row: unknown): string {
  if (!row || typeof row !== "object") return "";
  const raw = row as Record<string, unknown>;
  const v = raw.link ?? raw.Link ?? raw.url ?? raw.URL;
  return typeof v === "string" ? v.trim() : "";
}

/** Title of `screen.testYourKnowledge` (the Test Your Knowledge list screen). Used wherever that screen is linked in chrome (sidebar, hub, breadcrumbs). */
export const TEST_YOUR_KNOWLEDGE_SCREEN_LABEL = "Test Your Knowledge";

/** Stakeholder-filtered `operator.testYourKnowledge` rows. */
function testYourKnowledgeNavRows(payload: BuxusOperatorPayload, ctx: ResolveContext): unknown[] {
  const raw = getAt(payload, "operator.testYourKnowledge");
  if (!Array.isArray(raw)) return [];
  return asRows(raw).filter((r) => rowVisible(r, ctx.stakeholderCode));
}

/**
 * Returns the Test Your Knowledge **screen title** when the screen should appear (≥1 stakeholder-visible
 * TYK row). Row `linkTitle` / `LinkTitle` is only shown inside the screen body (`tykLinks`), not in the
 * nav tree. Web always routes to `screen.testYourKnowledge`; legacy RN may open the URL directly when
 * exactly one row exists — mobile can be aligned later via the shared contract.
 */
export function testYourKnowledgeNavLabel(payload: BuxusOperatorPayload, ctx: ResolveContext): string | null {
  const rows = testYourKnowledgeNavRows(payload, ctx);
  if (rows.length === 0) return null;
  return TEST_YOUR_KNOWLEDGE_SCREEN_LABEL;
}

/** Sets `nav.testKnowledge` to the Test Your Knowledge screen title when that screen is available. */
export function mergeTestYourKnowledgeMenuLabels(
  tree: MenuEntry[],
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
): MenuEntry[] {
  const label = testYourKnowledgeNavLabel(payload, ctx);
  if (!label) return tree;
  return tree.map((node) => {
    if (node.id === "nav.testKnowledge") {
      return { ...node, label };
    }
    if (node.children?.length) {
      return { ...node, children: mergeTestYourKnowledgeMenuLabels(node.children, payload, ctx) };
    }
    return node;
  });
}

function getPrimaryCorporateContactCard(
  payload: BuxusOperatorPayload,
  label = "Corporate Contact",
  jurisdictionKeys?: ReadonlySet<string> | null,
): { label: string; displayName: string; contactId: string } | null {
  const operator = payload.operator as Record<string, unknown>;
  const primary = operator.primaryCorporateContact as Record<string, unknown> | undefined;
  if (!primary || typeof primary !== "object") return null;

  let contacts = Array.isArray(operator.contacts)
    ? (operator.contacts as Record<string, unknown>[])
    : [];
  if (jurisdictionKeys && jurisdictionKeys.size > 0) {
    contacts = contacts.filter((c) => contactRowVisibleForJurisdictions(c, jurisdictionKeys));
  }

  const primaryId = typeof primary.id === "string" ? primary.id : undefined;
  const matched =
    (primaryId
      ? contacts.find((c) => typeof c?.id === "string" && c.id === primaryId)
      : undefined) ?? primary;

  const firstName =
    typeof matched.firstName === "string"
      ? matched.firstName
      : typeof primary.firstName === "string"
        ? primary.firstName
        : "";
  const lastName =
    typeof matched.lastName === "string"
      ? matched.lastName
      : typeof primary.lastName === "string"
        ? primary.lastName
        : "";
  const fullName = `${firstName} ${lastName}`.trim();
  const contactId =
    (typeof matched.id === "string" && matched.id.trim()) ||
    (typeof primary.id === "string" && primary.id.trim()) ||
    "";
  if (!fullName || !contactId) return null;

  return { label, displayName: fullName, contactId };
}

function compactRichHtmlBlocks(blocks: ResolvedBlock[]): ResolvedBlock[] {
  return blocks.map((b) =>
    b.kind === "richHtml" ? { ...b, html: compactRichHtml(b.html) } : b,
  );
}

function resolveSection(
  binding: SectionBinding,
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  isScreenVisible?: (screenId: string) => boolean,
  options?: ResolveScreenOptions,
): ResolvedBlock[] {
  switch (binding.kind) {
    case "mapPrimary":
      return [];
    case "htmlAt": {
      const html = getString(payload, binding.path);
      if (!html?.trim()) return [];
      return [{ kind: "richHtml", html: compactRichHtml(html) }];
    }
    case "htmlStakeholderRows": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      const parts = rows
        .map((r) => rowContent(r))
        .filter(Boolean);
      if (parts.length === 0) return [];
      if (binding.mergeRows === false) {
        return [{ kind: "richHtml", html: compactRichHtml(parts[0]!) }];
      }
      return [
        {
          kind: "richHtml",
          html: compactRichHtml(
            parts.join(
            '<hr style="margin:1.25rem 0;border:none;border-top:1px solid #e2e8f0"/>',
          ),
          ),
        },
      ];
    }
    case "linkList": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      const items: { label: string; url?: string }[] = [];
      for (const r of rows) {
        const label = rowLabel(r);
        const urlRaw = rowUrl(r);
        const url = typeof urlRaw === "string" && urlRaw.trim() ? normalizeWebUrl(urlRaw.trim()) : undefined;
        const labelTrim = typeof label === "string" ? label.trim() : "";
        if (!labelTrim && !url) continue;
        items.push({ label: labelTrim || url || "Link", ...(url ? { url } : {}) });
      }
      return items.length ? [{ kind: "links", items }] : [];
    }
    case "imageRefGallery": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw)) return [];
      const asset =
        binding.markerFilter === "asset"
          ? findActiveAssetRow(payload, ctx, options?.activeAssetId)
          : null;
      const items: { caption: string; ref: { id: string; fileExt?: string } }[] = [];
      for (const row of raw as Record<string, unknown>[]) {
        if (!markerImageRowVisible(row, binding.markerFilter, asset)) continue;
        const id = row[binding.idField];
        if (typeof id !== "string") continue;
        const cap = row[binding.captionField];
        const ext = binding.extField ? row[binding.extField] : undefined;
        items.push({
          caption: typeof cap === "string" ? cap : id,
          ref: {
            id,
            fileExt: typeof ext === "string" ? ext : undefined,
          },
        });
      }
      return items.length ? [{ kind: "gallery", items }] : [];
    }
    case "branchHub": {
      const introHtml = binding.intro
        ? stakeholderIntroHtml(
            payload,
            binding.intro.path,
            binding.intro.mergeRows !== false,
            ctx,
          )
        : undefined;
      const navItems = resolveHubNavItems(
        binding.items,
        payload,
        ctx,
        isScreenVisible ?? (() => true),
        options,
      );
      if (!introHtml && navItems.length === 0) return [];
      return [{ kind: "branchHub", ...(introHtml ? { introHtml } : {}), items: navItems }];
    }
    case "navButtons": {
      const navItems = resolveHubNavItems(
        binding.items,
        payload,
        ctx,
        isScreenVisible ?? (() => true),
        options,
      );
      return navItems.length ? [{ kind: "childNav", items: navItems }] : [];
    }
    case "staticHtml": {
      const html = binding.html?.trim();
      return html ? [{ kind: "richHtml", html: compactRichHtml(binding.html) }] : [];
    }
    case "inlineNotice": {
      const message = binding.message?.trim();
      if (!message) return [];
      return [{ kind: "noticeCard", message, tone: binding.tone ?? "neutral" }];
    }
    case "visibleIfArray": {
      return [];
    }
    case "htmlIfArray": {
      const v = getAt(payload, binding.path);
      if (!Array.isArray(v) || v.length === 0) return [];
      const html = binding.html?.trim();
      return html ? [{ kind: "richHtml", html: compactRichHtml(binding.html) }] : [];
    }
    case "primaryCorporateContact": {
      const card = getPrimaryCorporateContactCard(
        payload,
        binding.label,
        ctx.jurisdictionCountyStateKeys,
      );
      return card
        ? [
            {
              kind: "corporateContactCard",
              label: card.label,
              displayName: card.displayName,
              contactId: card.contactId,
            },
          ]
        : [];
    }
    case "companyOverviewDetails": {
      const html = getCompanyOverviewDetailsHtml(payload);
      return html ? [{ kind: "richHtml", html }] : [];
    }
    case "optionalCta": {
      const url =
        getString(payload, binding.urlPath)?.trim() ??
        firstStringAtPaths(payload, [
          "operator.privacyPolicyLink",
          "operator.privacyPolicyUrl",
          "operator.privacyURL",
          "operator.privacyUrl",
        ]);
      if (!url) return [];
      const out: ResolvedBlock[] = [];
      if (binding.heading?.trim()) {
        const h = htmlEscape(binding.heading.trim());
        out.push({
          kind: "richHtml",
          html: `<h3 style="font-size:13px;color:#64748b;font-weight:600;margin:1.35rem 0 0.55rem;letter-spacing:0.02em;line-height:1.3">${h}</h3>`,
        });
      }
      out.push({ kind: "ctaLink", label: binding.label, url: normalizeWebUrl(url) });
      return out;
    }
    case "optionalLink": {
      const url = getString(payload, binding.urlPath)?.trim();
      if (!url) return [];
      return [{ kind: "ctaLink", label: binding.label, url: normalizeWebUrl(url) }];
    }
    case "labeledHtmlAt": {
      const html = getString(payload, binding.path);
      if (!html?.trim()) return [];
      const h = htmlEscape(binding.heading);
      return [
        {
          kind: "richHtml",
          html: `<h3 style="font-size:13px;color:#64748b;font-weight:600;margin:1.35rem 0 0.55rem;letter-spacing:0.02em;line-height:1.3">${h}</h3>${compactRichHtml(html)}`,
        },
      ];
    }
    case "labeledStakeholderRows": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      const parts = rows
        .map((r) => rowContent(r))
        .filter(Boolean);
      if (parts.length === 0) return [];
      const inner =
        binding.mergeRows === false
          ? parts[0]!
          : parts.join(
              '<hr style="margin:1.25rem 0;border:none;border-top:1px solid #e2e8f0"/>',
            );
      const head = htmlEscape(binding.heading);
      return [
        {
          kind: "richHtml",
          html: `<h3 style="font-size:13px;color:#64748b;font-weight:600;margin:1.35rem 0 0.55rem;letter-spacing:0.02em;line-height:1.3">${head}</h3>${compactRichHtml(inner)}`,
        },
      ];
    }
    case "resourcesScreenLegacy": {
      const op = payload.operator as Record<string, unknown>;
      const linkRows = Array.isArray(op.resources) ? op.resources : [];
      const docRows = Array.isArray(op.lookupResources) ? op.lookupResources : [];
      const links: { label: string; url: string }[] = [];
      for (const r of linkRows) {
        if (!r || typeof r !== "object") continue;
        const o = r as Record<string, unknown>;
        const label = o.label ?? o.Label;
        const url = o.url ?? o.URL;
        if (typeof label === "string" && label.trim() && typeof url === "string" && url.trim()) {
          links.push({ label: label.trim(), url: normalizeWebUrl(url.trim()) });
        }
      }
      const docs: { id: string; label: string; fileExt?: string; fileName?: string }[] = [];
      for (const r of docRows) {
        if (!r || typeof r !== "object") continue;
        const o = r as Record<string, unknown>;
        const label = o.label ?? o.Label ?? o.title ?? o.Title;
        const id = o.id ?? o.Id;
        if (
          (typeof label === "string" && label.trim()) &&
          typeof id === "string" &&
          id.trim()
        ) {
          const extRaw = o.fileExt ?? o.FileExt;
          const ext = typeof extRaw === "string" ? extRaw : undefined;
          const explicitName = o.fileName ?? o.FileName;
          let fileName: string | undefined;
          if (typeof explicitName === "string" && explicitName.trim()) {
            fileName = explicitName.trim();
          } else if (ext && typeof label === "string") {
            const t = label.trim();
            const normExt = ext.startsWith(".") ? ext : `.${ext}`;
            fileName = t.toLowerCase().endsWith(normExt.toLowerCase()) ? t : `${t}${normExt}`;
          }
          docs.push({
            id: id.trim(),
            label: label.trim(),
            fileExt: ext,
            fileName,
          });
        }
      }
      const blocks: ResolvedBlock[] = [];
      if (links.length) blocks.push({ kind: "resourceSection", title: "Links", items: links });
      if (docs.length) blocks.push({ kind: "documentSection", title: "Documents", items: docs });
      return blocks;
    }
    case "filteredAssetNav": {
      const assets = getVisibleAssets(payload, ctx);
      if (!assets.length) return [];
      const intro = binding.introHtml?.trim();
      const items: ResolvedNavItem[] = [];
      for (const row of assets) {
        const pair = assetIdAndName(row);
        if (pair) {
          items.push({
            label: pair.label,
            targetScreenId: binding.targetScreenId,
            assetId: pair.id,
          });
        }
      }
      const out: ResolvedBlock[] = [];
      if (intro) out.push({ kind: "richHtml", html: intro });
      if (items.length) out.push({ kind: "childNav", items });
      return out;
    }
    case "optionalDynamicHubLink": {
      let title: string | undefined;
      for (const p of binding.titlePaths) {
        const s = getString(payload, p)?.trim();
        if (s) {
          title = s;
          break;
        }
      }
      let url: string | undefined;
      for (const p of binding.urlPaths) {
        const s = getString(payload, p)?.trim();
        if (s) {
          url = s;
          break;
        }
      }
      if (!title || !url) return [];
      return [{ kind: "hubExternalLink", label: title, url: normalizeWebUrl(url) }];
    }
    case "tykLinks": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      const items: { label: string; url?: string }[] = [];
      for (const r of rows) {
        const label = tykTitle(r) || tykUrl(r) || "Link";
        const urlRaw = tykUrl(r);
        const url = urlRaw ? normalizeWebUrl(urlRaw) : undefined;
        if (label.trim() || url) items.push({ label: label.trim() || "Link", ...(url ? { url } : {}) });
      }
      return items.length ? [{ kind: "links", items }] : [];
    }
    case "contactDirectory": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw)) return [];
      const jur = ctx.jurisdictionCountyStateKeys;
      const items = raw
        .filter((row) => contactRowVisibleForJurisdictions(row, jur))
        .map((row) => normalizeContactCard(row))
        .filter((x): x is ResolvedContactCard => x != null);

      if (options?.contactId) {
        const match = items.find((c) => c.id === options.contactId);
        if (!match) {
          return [
            {
              kind: "noticeCard",
              tone: "neutral",
              message: "Contact not found.",
            },
          ];
        }
        return [{ kind: "contactCards", items: [match] }];
      }

      if (options?.contactArea) {
        const area = options.contactArea;
        const filtered = items
          .filter((c) => (c.contactArea ?? DEFAULT_CONTACT_AREA) === area)
          .sort((a, b) => compareContactsForDisplay(a, b));
        if (!filtered.length) {
          return [
            {
              kind: "noticeCard",
              tone: "neutral",
              message: "No contacts in this area.",
            },
          ];
        }
        if (filtered.length <= CONTACT_INLINE_CARD_THRESHOLD) {
          return [{ kind: "contactCards", items: filtered }];
        }
        return [
          {
            kind: "childNav",
            items: filtered.map((c) => ({
              label: c.displayName,
              targetScreenId: "screen.contacts",
              contactId: c.id,
              contactArea: area,
            })),
          },
        ];
      }

      const areas = [
        ...new Set(items.map((c) => c.contactArea ?? DEFAULT_CONTACT_AREA)),
      ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }));

      if (!areas.length) return [];

      return [
        {
          kind: "childNav",
          items: areas.map((area) => ({
            label: area,
            targetScreenId: "screen.contacts",
            contactArea: area,
          })),
        },
      ];
    }
    case "makeARequestHub": {
      const operator = payload.operator as Record<string, unknown>;
      if (options?.requestType) {
        return [{ kind: "requestForm", requestType: options.requestType }];
      }
      const buttons = visibleMakeARequestOptions(operator, ctx.stakeholderCode).map((opt) => ({
        requestType: opt.requestType,
        label: opt.label,
      }));
      const emergencyNumber = operatorEmergencyNumber(operator);
      return buttons.length || emergencyNumber
        ? [{ kind: "makeARequestHub", emergencyNumber, buttons }]
        : [
            {
              kind: "noticeCard",
              tone: "neutral",
              message: "No request options are available for your role.",
            },
          ];
    }
    case "bulletListFromArray": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw) || raw.length === 0) return [];
      const labels: string[] = [];
      for (const item of raw) {
        if (!item || typeof item !== "object") continue;
        const o = item as Record<string, unknown>;
        const v = o[binding.labelKey];
        if (typeof v === "string" && v.trim()) labels.push(v.trim());
      }
      if (!labels.length) return [];
      const intro = binding.introHtml?.trim();
      const ul =
        "<ul style=\"margin:8px 0 0;padding-left:1.25rem;line-height:1.5\">" +
        labels.map((l) => `<li>${htmlEscape(l)}</li>`).join("") +
        "</ul>";
      const html = (intro ? `${intro}\n` : "") + ul;
      return [{ kind: "richHtml", html }];
    }
    case "assetOverviewHubNav": {
      const items = buildAssetOverviewHubNav(payload, ctx, options?.activeAssetId);
      return items.length ? [{ kind: "childNav", items }] : [];
    }
    case "assetProfileDetails": {
      const asset = findActiveAssetRow(payload, ctx, options?.activeAssetId);
      if (!asset) return [];
      const blocks: ResolvedBlock[] = [];
      const card = buildFacilityProfileCardModel(asset);
      if (card) blocks.push({ kind: "facilityProfileCard", model: card });
      const html = buildAssetProfileDetailsHtml(asset);
      if (html) blocks.push({ kind: "richHtml", html });
      return blocks;
    }
    case "assetEmergencyHubNav": {
      const items = buildAssetEmergencyHubNavItems(payload, ctx, options?.activeAssetId);
      return items.length ? [{ kind: "childNav", items }] : [];
    }
    case "productsHazardsCatalog": {
      const model = buildProductsHazardsBrowse(payload, ctx, options?.activeAssetId);
      if (!model) {
        return [
          {
            kind: "noticeCard",
            tone: "neutral",
            message: "No hazards listed for this asset.",
          },
        ];
      }
      return [{ kind: "productsHazardsBrowse", model }];
    }
    case "assetEmergencyReportBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyReportBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyStepsBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyStepsBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyResponderBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyResponderBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyCompanyBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyCompanyBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyCommunicationsBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyCommunicationsBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyFireSuppressionBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyFireSuppressionBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyWorstCaseBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyWorstCaseBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetEmergencyKeyLocationsBody": {
      return compactRichHtmlBlocks(
        buildAssetEmergencyKeyLocationsBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetHazardDetailBody": {
      const model = buildAssetHazardDetailModel(
        payload,
        ctx,
        options?.activeAssetId,
        options?.hazardId ?? null,
      );
      if (!model) {
        return [
          {
            kind: "noticeCard",
            tone: "neutral",
            message: "Hazard not found for this asset.",
          },
        ];
      }
      return [{ kind: "hazardDetail", model }];
    }
    case "assetDocumentsLegacyOutline":
    case "assetDocumentsListBody": {
      return buildAssetDocumentsListBlocks(payload, ctx, options?.activeAssetId);
    }
    case "assetDirectoryListBody": {
      return compactRichHtmlBlocks(
        buildAssetDirectoryListBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetGeneralHazardsLegacyBody": {
      return compactRichHtmlBlocks(
        buildAssetGeneralHazardsBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    case "assetSpecialNotesLegacyBody": {
      return compactRichHtmlBlocks(
        buildAssetSpecialNotesBlocks(payload, ctx, options?.activeAssetId),
      );
    }
    default: {
      return [];
    }
  }
}

function sectionHasRenderableContent(
  binding: SectionBinding,
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  isScreenVisible: (screenId: string) => boolean,
  options?: ResolveScreenOptions,
): boolean {
  switch (binding.kind) {
    case "htmlAt": {
      const html = getString(payload, binding.path);
      return !!html?.trim();
    }
    case "htmlStakeholderRows": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      const parts = rows
        .map((r) => rowContent(r).trim())
        .filter(Boolean);
      return parts.length > 0;
    }
    case "linkList": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      for (const r of rows) {
        const label = rowLabel(r).trim();
        const url = rowUrl(r).trim();
        if (label || url) return true;
      }
      return false;
    }
    case "imageRefGallery": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw)) return false;
      const asset =
        binding.markerFilter === "asset"
          ? findActiveAssetRow(payload, ctx, options?.activeAssetId)
          : null;
      return raw.some((row) => {
        if (!row || typeof row !== "object") return false;
        const r = row as Record<string, unknown>;
        if (!markerImageRowVisible(r, binding.markerFilter, asset)) return false;
        return typeof r[binding.idField] === "string";
      });
    }
    case "branchHub": {
      const hasIntro =
        binding.intro != null &&
        !!stakeholderIntroHtml(
          payload,
          binding.intro.path,
          binding.intro.mergeRows !== false,
          ctx,
        );
      const hasNav = binding.items.some(
        (i) =>
          (i.assetId && i.assetId.length > 0) || isScreenVisible(i.targetScreenId),
      );
      return hasIntro || hasNav;
    }
    case "navButtons": {
      return binding.items.some(
        (i) =>
          (i.assetId && i.assetId.length > 0) || isScreenVisible(i.targetScreenId),
      );
    }
    case "staticHtml": {
      return !!binding.html?.trim();
    }
    case "inlineNotice": {
      return !!binding.message?.trim();
    }
    case "visibleIfArray": {
      const v = getAt(payload, binding.path);
      return Array.isArray(v) && v.length > 0;
    }
    case "mapPrimary":
      return true;
    case "productsHazardsCatalog": {
      const asset = findActiveAssetRow(payload, ctx, options?.activeAssetId);
      if (asset) return checkHasHazardNavAsset(asset);
      return getVisibleAssets(payload, ctx).some((row) =>
        checkHasHazardNavAsset(row as Record<string, unknown>),
      );
    }
    case "htmlIfArray": {
      const v = getAt(payload, binding.path);
      if (!Array.isArray(v) || v.length === 0) return false;
      return !!binding.html?.trim();
    }
    case "primaryCorporateContact": {
      return !!getPrimaryCorporateContactCard(
        payload,
        binding.label,
        ctx.jurisdictionCountyStateKeys,
      );
    }
    case "companyOverviewDetails": {
      return !!getCompanyOverviewDetailsHtml(payload);
    }
    case "optionalCta": {
      const url =
        getString(payload, binding.urlPath)?.trim() ??
        firstStringAtPaths(payload, [
          "operator.privacyPolicyLink",
          "operator.privacyPolicyUrl",
          "operator.privacyURL",
          "operator.privacyUrl",
        ]);
      return !!url?.trim();
    }
    case "optionalLink": {
      return !!getString(payload, binding.urlPath)?.trim();
    }
    case "labeledHtmlAt": {
      const html = getString(payload, binding.path);
      return !!html?.trim();
    }
    case "labeledStakeholderRows": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      return rows.some((r) => !!rowContent(r).trim());
    }
    case "resourcesScreenLegacy": {
      const { links, docs } = countResourceMenuRows(payload.operator as Record<string, unknown>);
      return links > 0 || docs > 0;
    }
    case "filteredAssetNav": {
      return getVisibleAssets(payload, ctx).length > 0;
    }
    case "optionalDynamicHubLink": {
      const op = payload;
      const hasTitle = binding.titlePaths.some((p) => !!getString(op, p)?.trim());
      const hasUrl = binding.urlPaths.some((p) => !!getString(op, p)?.trim());
      return hasTitle && hasUrl;
    }
    case "tykLinks": {
      const rows = asRows(getAt(payload, binding.path)).filter((r) =>
        rowVisible(r, ctx.stakeholderCode),
      );
      return rows.some((r) => !!(tykTitle(r) || tykUrl(r)));
    }
    case "contactDirectory": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw)) return false;
      const jur = ctx.jurisdictionCountyStateKeys;
      return raw.some(
        (row) =>
          contactRowVisibleForJurisdictions(row, jur) && normalizeContactCard(row) != null,
      );
    }
    case "makeARequestHub": {
      const operator = payload.operator as Record<string, unknown>;
      return (
        hasAnyMakeARequestAccess(operator, ctx.stakeholderCode) ||
        !!operatorEmergencyNumber(operator)
      );
    }
    case "bulletListFromArray": {
      const raw = getAt(payload, binding.path);
      if (!Array.isArray(raw) || raw.length === 0) return false;
      return raw.some((item) => {
        if (!item || typeof item !== "object") return false;
        const o = item as Record<string, unknown>;
        const v = o[binding.labelKey];
        return typeof v === "string" && v.trim().length > 0;
      });
    }
    case "assetOverviewHubNav":
    case "assetProfileDetails":
    case "assetEmergencyHubNav":
    case "assetHazardDetailBody":
      return true;
    case "assetEmergencyReportBody":
      return buildAssetEmergencyReportBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyStepsBody":
      return buildAssetEmergencyStepsBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyResponderBody":
      return buildAssetEmergencyResponderBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyCommunicationsBody":
      return buildAssetEmergencyCommunicationsBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyFireSuppressionBody":
      return buildAssetEmergencyFireSuppressionBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyWorstCaseBody":
      return buildAssetEmergencyWorstCaseBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyKeyLocationsBody":
      return buildAssetEmergencyKeyLocationsBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetEmergencyCompanyBody": {
      return hasAssetEmergencyCompanyScreenContent(
        payload,
        ctx,
        options?.activeAssetId ?? ctx.activeAssetId,
      );
    }
    case "assetDocumentsLegacyOutline":
    case "assetDocumentsListBody": {
      const assetId = options?.activeAssetId?.trim();
      if (!assetId) return false;
      const asset = findActiveAssetRow(payload, ctx, assetId);
      if (!asset) return false;
      if (ctx.hasLocalAssetDocuments != null) {
        return hasAssetDocumentsNavVisible(asset, assetId, ctx);
      }
      const model = buildAssetDocumentsListModel(payload, ctx, assetId);
      return !!model && model.sections.some((section) => section.rows.length > 0);
    }
    case "assetDirectoryListBody":
      return buildAssetDirectoryListBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetGeneralHazardsLegacyBody":
      return buildAssetGeneralHazardsBlocks(payload, ctx, options?.activeAssetId).length > 0;
    case "assetSpecialNotesLegacyBody":
      return buildAssetSpecialNotesBlocks(payload, ctx, options?.activeAssetId).length > 0;
    default: {
      return false;
    }
  }
}

function createScreenVisibilityResolver(
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
): (screenId: string) => boolean {
  const state: VisibilityState = { cache: new Map(), visiting: new Set() };
  const isVisible = (screenId: string): boolean => {
    const cached = state.cache.get(screenId);
    if (cached != null) return cached;

    const gated = legacyOperatorScreenGate(screenId, payload, ctx);
    if (gated === false) {
      state.cache.set(screenId, false);
      return false;
    }
    if (gated === true) {
      state.cache.set(screenId, true);
      return true;
    }

    const def = screens[screenId];
    if (!def) {
      state.cache.set(screenId, false);
      return false;
    }
    if (state.visiting.has(screenId)) {
      // Break accidental cycles in nav graph.
      return false;
    }
    state.visiting.add(screenId);
    const visibilityOptions = { activeAssetId: ctx.activeAssetId ?? undefined };
    const visible = def.sections.some((s) =>
      sectionHasRenderableContent(s, payload, ctx, isVisible, visibilityOptions),
    );
    state.visiting.delete(screenId);
    state.cache.set(screenId, visible);
    return visible;
  };
  return isVisible;
}

export function isScreenVisible(
  screenId: string,
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
): boolean {
  return createScreenVisibilityResolver(payload, ctx)(screenId);
}

export function resolveScreenTitle(
  def: ScreenDefinition,
  payload: BuxusOperatorPayload,
  options?: ResolveScreenOptions,
  ctx?: ResolveContext,
): string {
  if (ctx && def.id === "screen.testYourKnowledge") {
    const dyn = testYourKnowledgeNavLabel(payload, ctx);
    if (dyn) return dyn;
  }
  if (options?.activeAssetId && ctx) {
    const assets = getVisibleAssets(payload, ctx);
    const row = assets.find(
      (a) => typeof a.id === "string" && a.id === options.activeAssetId,
    );
    if (row && def.id === "screen.assetHazard") {
      if (options.hazardId?.trim()) {
        const hz = findHazardRowInAsset(row as Record<string, unknown>, options.hazardId.trim());
        if (hz) return hazardDetailTitleFromRow(hz);
        return "Hazard";
      }
    }
    if (row && def.id === "screen.assetProfile") {
      return assetProfileNavLabel(row as Record<string, unknown>);
    }
  }
  if (def.title) return def.title;
  if (def.titlePath) {
    const s = getString(payload, def.titlePath);
    if (s?.trim()) return s;
  }
  return def.titleFallback ?? def.id;
}

export function resolveScreen(
  screenId: string,
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  options?: ResolveScreenOptions,
): { title: string; blocks: ResolvedBlock[] } | null {
  const def = screens[screenId];
  if (!def) return null;
  const isVisible = createScreenVisibilityResolver(payload, ctx);
  if (!isVisible(screenId)) return null;
  const blocks: ResolvedBlock[] = [];
  for (const s of def.sections) {
    blocks.push(...resolveSection(s, payload, ctx, isVisible, options));
  }
  return { title: resolveScreenTitle(def, payload, options, ctx), blocks };
}

function screenUsesMapPrimary(screenId: string): boolean {
  const def = screens[screenId];
  return !!def?.sections.some((s) => s.kind === "mapPrimary");
}

/** True when the screen is visible and will render map UI and/or resolved blocks for this session. */
export function screenHasResolvableContent(
  screenId: string,
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  options?: ResolveScreenOptions,
): boolean {
  if (!isScreenVisible(screenId, payload, ctx)) return false;
  if (screenUsesMapPrimary(screenId)) {
    const activeAssetId = options?.activeAssetId ?? ctx.activeAssetId;
    if (
      screenId === "screen.assetOverview" &&
      activeAssetId &&
      !findActiveAssetRow(payload, ctx, activeAssetId)
    ) {
      return false;
    }
    return true;
  }
  const resolved = resolveScreen(screenId, payload, ctx, options);
  return !!(resolved && resolved.blocks.length > 0);
}

function resolveOptionsForScreen(
  screenId: string,
  activeAssetId: string | null | undefined,
  extra?: Omit<ResolveScreenOptions, "activeAssetId">,
): ResolveScreenOptions | undefined {
  const base = extra ? { ...extra } : {};
  if (activeAssetId && screenId.startsWith("screen.asset") && screenId !== "screen.assets") {
    return { ...base, activeAssetId };
  }
  return Object.keys(base).length > 0 ? base : undefined;
}

/**
 * After stakeholder switch, deep routes may be invisible or resolve to empty blocks (e.g. asset
 * filtered out). Walk up the nav stack to the first resolvable screen; default Operator Overview.
 */
export function findStakeholderSafeNavStack(
  stack: string[],
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  activeAssetId?: string | null,
  extraOptions?: Omit<ResolveScreenOptions, "activeAssetId">,
): string[] | null {
  const current = stack[stack.length - 1];
  if (!current?.startsWith("screen.")) return null;

  const optsFor = (screenId: string) =>
    resolveOptionsForScreen(screenId, activeAssetId, extraOptions);

  if (screenHasResolvableContent(current, payload, ctx, optsFor(current))) return null;

  for (let i = stack.length - 2; i >= 0; i--) {
    const sid = stack[i]!;
    if (!sid.startsWith("screen.")) continue;
    if (screenHasResolvableContent(sid, payload, ctx, optsFor(sid))) {
      return stack.slice(0, i + 1);
    }
  }

  return ["screen.operatorOverview"];
}

/**
 * HTML body fragment for `react-native-render-html` (skips `childNav`; no outer document).
 * Also used to build the legacy WebView document wrapper below.
 */
export function blocksToHtmlBodyFragment(blocks: ResolvedBlock[]): string {
  const esc = (s: string) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  const escAttr = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  const body: string[] = [];
  for (const b of blocks) {
    if (
      b.kind === "childNav" ||
      b.kind === "branchHub" ||
      b.kind === "productsHazardsBrowse" ||
      b.kind === "hazardDetail" ||
      b.kind === "assetDocumentsList" ||
      b.kind === "companyErpPicker" ||
      b.kind === "companyResponseAccordions" ||
      b.kind === "responderActionsAccordions" ||
      b.kind === "radioFrequencyAccordions" ||
      b.kind === "keyLocationAccordions" ||
      b.kind === "emergencyCallButton"
    ) {
      continue;
    }
    if (b.kind === "contactCards") {
      body.push(
        '<div style="margin:12px 0;">' +
          b.items
            .map((c) => {
              const lines = [
                `<div style="font-weight:700;font-size:17px;margin-bottom:6px">${esc(c.displayName)}</div>`,
              ];
              if (c.title) {
                lines.push(
                  `<div style="font-size:14px;color:#64748b;margin-bottom:6px">${esc(c.title)}</div>`,
                );
              }
              if (c.contactType) {
                lines.push(
                  `<div style="font-size:13px;color:#64748b;margin-bottom:4px">${esc(c.contactType)}</div>`,
                );
              }
              if (c.email) {
                lines.push(
                  `<div style="margin-top:6px"><span style="color:#64748b;font-size:13px">Email </span><a href="mailto:${escAttr(c.email)}">${esc(c.email)}</a></div>`,
                );
              }
              if (c.officePhone) {
                lines.push(
                  `<div style="margin-top:4px"><span style="color:#64748b;font-size:13px">Office </span><a href="tel:${escAttr(c.officePhone)}">${esc(c.officePhone)}</a></div>`,
                );
              }
              if (c.cellPhone) {
                lines.push(
                  `<div style="margin-top:4px"><span style="color:#64748b;font-size:13px">Cell </span><a href="tel:${escAttr(c.cellPhone)}">${esc(c.cellPhone)}</a></div>`,
                );
              }
              if (c.hourEmergency) {
                lines.push(
                  `<div style="margin-top:4px"><span style="color:#64748b;font-size:13px">24hr Emergency </span>${esc(c.hourEmergency)}</div>`,
                );
              }
              if (c.emergencyPhoneNumber) {
                lines.push(
                  `<div style="margin-top:4px"><span style="color:#64748b;font-size:13px">Emergency phone </span><a href="tel:${escAttr(c.emergencyPhoneNumber)}">${esc(c.emergencyPhoneNumber)}</a></div>`,
                );
              }
              if (c.url) {
                const href = normalizeWebUrl(c.url);
                lines.push(
                  `<div style="margin-top:4px"><a href="${escAttr(href)}" target="_blank" rel="noopener noreferrer">${esc(c.url)}</a></div>`,
                );
              }
              return (
                `<div style="padding:14px 16px;margin-bottom:12px;border:1px solid #e2e8f0;border-radius:10px;background:#fafafa">${lines.join("")}</div>`
              );
            })
            .join("") +
          "</div>",
      );
      continue;
    }
    if (b.kind === "hubExternalLink") {
      body.push(
        `<div style="margin:12px 0;"><a href="${escAttr(b.url)}" target="_blank" rel="noopener noreferrer" style="font-weight:600;color:#0ea5e9">${esc(b.label)}</a></div>`,
      );
      continue;
    }
    if (b.kind === "noticeCard") {
      const background = b.tone === "warning" ? "#fff7ed" : "#f8fafc";
      const border = b.tone === "warning" ? "#fed7aa" : "#e2e8f0";
      const text = b.tone === "warning" ? "#9a3412" : "#475569";
      body.push(
        `<p style="padding:10px 12px;background:${background};border:1px solid ${border};border-radius:8px;font-size:14px;color:${text};margin:0 0 12px">${esc(b.message)}</p>`,
      );
      continue;
    }
    if (b.kind === "ctaLink") {
      body.push(
        `<div style="margin:16px 0;"><a href="${escAttr(b.url)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 20px;background:#fc0e2c;color:#fff;font-weight:700;border-radius:8px;text-decoration:none">${esc(b.label)}</a></div>`,
      );
      continue;
    }
    if (b.kind === "resourceSection") {
      const head = esc(b.title);
      body.push(
        `<h3 style="font-size:16px;font-weight:700;text-align:center;margin:16px 0 10px">${head}</h3><ul style="padding-left:1.25rem;font-size:16px;line-height:1.5;">` +
          b.items
            .map((i) => `<li><a href="${escAttr(i.url)}">${esc(i.label)}</a></li>`)
            .join("") +
          "</ul>",
      );
      continue;
    }
    if (b.kind === "documentSection") {
      const head = esc(b.title);
      body.push(
        `<h3 style="font-size:16px;font-weight:700;text-align:center;margin:20px 0 10px">${head}</h3>` +
          `<div style="font-size:15px;line-height:1.5;color:#64748b">` +
          b.items
            .map(
              (d) =>
                `<p style="margin:8px 0;padding:10px 12px;border:1px solid #e2e8f0;border-radius:8px;background:#fafafa"><strong>${esc(d.label)}</strong><br/><span style="font-size:13px">File ${esc(d.id)}${esc(d.fileExt ?? "")} · download in app</span></p>`,
            )
            .join("") +
          "</div>",
      );
      continue;
    }
    if (b.kind === "richHtml") {
      body.push(`<section>${b.html}</section>`);
    }
    if (b.kind === "links") {
      body.push(
        '<ul style="padding-left:1.25rem;font-size:16px;line-height:1.5;">' +
          b.items
            .map((i) =>
              i.url?.trim()
                ? `<li><a href="${escAttr(i.url)}">${esc(i.label)}</a></li>`
                : `<li><span style="color:#0f172a;font-weight:600">${esc(i.label)}</span> <span style="color:#64748b;font-size:13px">(no URL in operator data)</span></li>`,
            )
            .join("") +
          "</ul>",
      );
      continue;
    }
    if (b.kind === "gallery") {
      body.push(
        '<div style="font-size:15px;line-height:1.45;">' +
          b.items
            .map(
              (i) =>
                `<p style="margin:16px 0;"><strong>${esc(i.caption)}</strong><br/><span style="color:#64748b">Blob ref: ${esc(i.ref.id)}${esc(i.ref.fileExt ?? "")}</span></p>`,
            )
            .join("") +
          "</div>",
      );
    }
  }
  return body.join("");
}

/** Full HTML document (e.g. mailto preview); prefer `blocksToHtmlBodyFragment` + RenderHtml on mobile. */
export function blocksToMobileDocument(blocks: ResolvedBlock[]): string {
  const inner = blocksToHtmlBodyFragment(blocks);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1"/><style>body{margin:12px;font-family:-apple-system;} a{color:#0ea5e9;}</style></head><body>${inner}</body></html>`;
}

/** Split blocks so each `childNav` can be rendered natively between HTML bundles (mobile). */
export type MobileShellPart =
  | { type: "htmlBundle"; blocks: ResolvedBlock[] }
  | { type: "childNav"; items: ResolvedNavItem[] };

export function partitionBlocksForMobile(blocks: ResolvedBlock[]): MobileShellPart[] {
  const parts: MobileShellPart[] = [];
  let buf: ResolvedBlock[] = [];
  const flush = () => {
    if (buf.length) {
      parts.push({ type: "htmlBundle", blocks: [...buf] });
      buf = [];
    }
  };
  for (const b of blocks) {
    if (b.kind === "branchHub") {
      flush();
      if (b.introHtml?.trim()) {
        parts.push({ type: "htmlBundle", blocks: [{ kind: "richHtml", html: b.introHtml }] });
      }
      if (b.items.length > 0) {
        parts.push({ type: "childNav", items: b.items });
      }
    } else if (b.kind === "childNav") {
      flush();
      parts.push({ type: "childNav", items: b.items });
    } else {
      buf.push(b);
    }
  }
  flush();
  return parts;
}

export function flattenMenu(
  entries: MenuEntry[],
  depth = 0,
): { id: string; label: string; screenId?: string; depth: number }[] {
  const out: { id: string; label: string; screenId?: string; depth: number }[] = [];
  for (const e of entries) {
    out.push({
      id: e.id,
      label: e.label,
      screenId: e.screenId,
      depth,
    });
    if (e.children?.length) {
      out.push(...flattenMenu(e.children, depth + 1));
    }
  }
  return out;
}

export function flattenMenuForPayload(
  entries: MenuEntry[],
  payload: BuxusOperatorPayload,
  ctx: ResolveContext,
  depth = 0,
): { id: string; label: string; screenId?: string; depth: number }[] {
  const out: { id: string; label: string; screenId?: string; depth: number }[] = [];
  const isVisible = createScreenVisibilityResolver(payload, ctx);

  const walk = (nodes: MenuEntry[], d: number) => {
    for (const e of nodes) {
      const childRowsStart = out.length;
      if (e.children?.length) {
        walk(e.children, d + 1);
      }
      const childRows = out.length - childRowsStart;
      const screenVisible = e.screenId ? isVisible(e.screenId) : false;
      const keep = screenVisible || childRows > 0 || (!e.screenId && !e.children?.length);
      if (!keep) {
        out.splice(childRowsStart, childRows);
        continue;
      }
      out.splice(childRowsStart, 0, {
        id: e.id,
        label: e.label,
        screenId: screenVisible ? e.screenId : undefined,
        depth: d,
      });
    }
  };

  walk(mergeTestYourKnowledgeMenuLabels(entries, payload, ctx), depth);
  return out;
}

export { menuTree, screens, MAP_PIPELINE_DISCLAIMER_HTML, MAP_PIPELINE_DISCLAIMER_TEXT };
